import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { useLanguage } from "@/contexts/LanguageContext";
import AuthShell from "@/components/AuthShell";
import LanguageSelector from "@/components/LanguageSelector";
import { ArrowRight, Loader2, Lock, Mail } from "lucide-react";
import bigTaxiDriverLogo from "@/assets/big-taxi-driver-logo.png";


import { API_BASE_URL as API } from "../apiConfig";
export default function LoginPage({ onLogin, appMode = "web" }) {
  const { t } = useLanguage();
  const isAdminApp = appMode === "admin";
  const adminAppTitle = "管理员后台";
  const adminAppSubtitle = "手机专用运营工作台";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setError("");
    setLoading(true);

    const form = e?.currentTarget;
    const submittedIdentifier = form?.elements?.email?.value ?? email;
    const submittedPassword = form?.elements?.password?.value ?? password;

    try {
      const response = await axios.post(`${API}/auth/login`, {
        email: submittedIdentifier.trim().toLowerCase(),
        password: submittedPassword,
      });

      const { user, access_token } = response.data;
      
      // 先存储到localStorage（同步）
      localStorage.setItem("token", access_token);
      localStorage.setItem("user", JSON.stringify(user));
      
      // 调用父组件更新state
      onLogin(user, access_token);
      
      // 延迟导航确保state更新完成
      setTimeout(() => {
        navigate({ pathname: '/', search: window.location.search }, { replace: true });
      }, 50);
    } catch (err) {
      setError(
        err.response?.data?.detail || t('loginFailed')
      );
      setLoading(false);
    }
  };

  return (
    <AuthShell
      brandLogo={isAdminApp ? bigTaxiDriverLogo : undefined}
      eyebrow={isAdminApp ? "BIG TAXI" : "SHONAN INTERNATIONAL"}
      title={isAdminApp ? adminAppTitle : t('loginTitle')}
      subtitle={isAdminApp ? adminAppSubtitle : t('authPlatformLine')}
      languageSelector={isAdminApp ? null : <LanguageSelector variant="large" />}
    >
      <div className={`auth-card ${isAdminApp ? "admin-app-login-card" : ""}`}>
        <div className="mb-8">
          <h2 className="auth-form-title">{isAdminApp ? "管理员登录" : t('loginPanelTitle')}</h2>
          <p className="auth-form-kicker">{isAdminApp ? "进入当日订单、次日订单和司机管理" : t('loginPanelSubtitle')}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="rounded-xl border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
              {error}
            </div>
          )}

          <div>
            <label className="mb-2 block text-sm font-medium text-foreground/80">
              {t('email')}
            </label>
            <div className="relative">
              <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                name="email"
                type="text"
                inputMode="text"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="premium-input pl-10"
                placeholder="email / phone"
              />
            </div>
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-foreground/80">
              {t('password')}
            </label>
            <div className="relative">
              <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                name="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="premium-input pl-10"
                placeholder="••••••"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="premium-button auth-login-button inline-flex w-full items-center justify-center gap-2 bg-primary text-primary-foreground shadow-[0_18px_36px_-18px_hsl(var(--primary)/0.75)] hover:scale-[1.025] hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:scale-100"
            data-testid="admin-login-button"
          >
            <span>{loading ? t('loading') : t('loginSystem')}</span>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
          </button>
        </form>

        {!isAdminApp && (
        <div className="mt-6 text-center">
          <p className="text-sm text-muted-foreground">
            {t('noAccount')}
            <Link
              to="/register"
              className="ml-1 font-semibold text-primary hover:text-primary/80"
            >
              {t('registerNow')}
            </Link>
          </p>
        </div>
        )}
      </div>
    </AuthShell>
  );
}
